import { z } from 'zod';
import { useSearchParams } from 'utils/navigation';
import { guidedSchema } from './useGuidedModeParam';

export const useTabsState = <
  const Tab extends string,
  Schema extends z.ZodType<
    { tab: Tab; guided?: z.infer<typeof guidedSchema>['guided'] },
    any,
    any
  >,
>(
  tabs: Array<{ key: Tab; label: string; isActive?: boolean }>,
  schema: Schema,
) => {
  const {
    searchParams: { guided },
  } = useSearchParams(guidedSchema);
  const { searchParams, navigate } = useSearchParams(
    schema.catch({ tab: tabs[0].key, guided }),
  );

  const requestedTab = searchParams.tab;
  const activeTab = tabs.some((tab) => tab.key === requestedTab)
    ? requestedTab
    : tabs[0]?.key ?? requestedTab;
  const activeTabIndex = tabs.findIndex((tab) => tab.key === activeTab);
  const previousTab =
    activeTabIndex > 0 ? tabs[activeTabIndex - 1].key : undefined;
  const nextTab =
    activeTabIndex < tabs.length - 1 ? tabs[activeTabIndex + 1].key : undefined;

  const goTo = (tab: Tab) => {
    navigate({ params: { tab, guided: searchParams.guided }, replace: true });
  };

  const next = () => {
    if (nextTab) {
      goTo(nextTab);
      window.scrollTo({ top: 0 });
    }
  };

  const back = () => {
    if (previousTab) {
      goTo(previousTab);
    }
  };

  return {
    tabs,
    activeTab,
    goTo,
    next,
    back,
    isFirst: !previousTab,
    isLast: !nextTab,
  };
};
